/** @jsxImportSource @emotion/react */

import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useParams } from 'react-router-dom'
import { findOneUser_findOneUser as User } from '../../__generatedTypes__/findOneUser'
import { WEB_APP_ORIGIN } from '../../pagesNew/Users'
import { useLink } from '../../routes/cmsRoutes'

type Props = {
  children: ReactNode
}
export const IMPERSONATE_LOCAL_STORAGE = 'impersonate-user'
export const IMPERSONATE_COOKIE = 'impersonate-user'

const WEB_CLASSROOM_LINK = ':platform/classroom'

export const getDomain = () => {
  const url = window.location.hostname.split('.')
  return url.slice(url.length - 2).join('.')
}

type ImpersonateUser = Pick<User, 'id' | 'email' | 'firstName' | 'lastName' | 'role'>

const parseImpersonateUser = (value: string): ImpersonateUser | null => {
  if (!value) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

type Context = {
  impersonateUser: ImpersonateUser | null
  changeImpersonateUser: (user: ImpersonateUser | null) => void
  openWebPage: () => void
}

const ImpersonateContext = createContext<Context>({
  impersonateUser: null,
  changeImpersonateUser: () => void 0,
  openWebPage: () => void 0,
})

export const ImpersonateProvider: FC<Props> = ({ children }) => {
  const { getLink } = useLink()
  const { platform = 'dat' } = useParams()
  const [impersonateUser, setImpersonateUser] = useState<ImpersonateUser | null>(null)

  useEffect(() => {
    const impersonateUserLsValue = localStorage.getItem(IMPERSONATE_LOCAL_STORAGE) ?? ''
    const impersonateUserCookieValue = Cookies.get(IMPERSONATE_COOKIE) ?? ''
    const userLsValue = parseImpersonateUser(impersonateUserLsValue)
    const userCookieValue = parseImpersonateUser(impersonateUserCookieValue)

    if (userLsValue) {
      if (userLsValue.id !== userCookieValue?.id) {
        changeImpersonateUser(null)
      } else {
        setImpersonateUser(userLsValue)
      }
    } else if (impersonateUserLsValue || impersonateUserCookieValue) {
      changeImpersonateUser(null)
    }

    function handleStorage(event: StorageEvent) {
      if (event.key === IMPERSONATE_LOCAL_STORAGE) {
        setImpersonateUser(event.newValue ? parseImpersonateUser(event.newValue) : null)
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const changeImpersonateUser = useCallback((user: ImpersonateUser | null) => {
    if (user) {
      const impersonateUserValue = JSON.stringify(user)
      localStorage.setItem(IMPERSONATE_LOCAL_STORAGE, impersonateUserValue)
      Cookies.set(IMPERSONATE_COOKIE, impersonateUserValue, { path: '/', domain: getDomain() })
      setImpersonateUser(user)
    } else {
      localStorage.removeItem(IMPERSONATE_LOCAL_STORAGE)
      Cookies.remove(IMPERSONATE_COOKIE, { path: '/', domain: getDomain() })
      setImpersonateUser(null)
    }
  }, [])

  const openWebPage = useCallback(() => {
    window.open(
      `${WEB_APP_ORIGIN}/${getLink(WEB_CLASSROOM_LINK, {
        platform,
      })}`,
      '_blank'
    )
  }, [])

  return (
    <ImpersonateContext.Provider
      value={{
        impersonateUser,
        changeImpersonateUser,
        openWebPage,
      }}
    >
      {children}
    </ImpersonateContext.Provider>
  )
}

export const useImpersonateUser = () => useContext(ImpersonateContext)
